import { useEffect, useRef, useState } from 'react';
import {
  fetchAdminRecipes,
  updateAdminRecipe,
  type AdminRecipeListing,
  type AdminRecipeRow,
} from '../../api/admin';
import type { RecipeState } from '../../api/missions';
import { ca } from '../../i18n/ca';
import { errorText, fmtWhen } from './shared';

interface Props {
  onError: (text: string | null) => void;
}

/** What a row's fields hold while they differ from what the platform keeps. */
interface Draft {
  cards?: string;
  note?: string;
}

type Change = { state?: RecipeState; cardsRequired?: number; note?: string };

const STATES: RecipeState[] = ['ready', 'in_tests', 'coming'];

type SortKey = 'recipe' | 'state' | 'progress' | 'cards' | 'decided';
type Sort = { key: SortKey; dir: 'asc' | 'desc' };

/** How far along a recipe is: ready over in tests over coming, and one that can be generated over one that cannot. */
function stateRank(row: AdminRecipeRow): number {
  const rank = row.state === 'ready' ? 3 : row.state === 'in_tests' ? 2 : 1;
  return rank * 2 + (row.generatable ? 1 : 0);
}

function progressOf(row: AdminRecipeRow): number {
  if (!row.generatable) return -1;
  return row.cardsRequired > 0 ? row.testsPassed / row.cardsRequired : row.testsPassed;
}

/**
 * The rows in the order asked for. Ties always fall back to the most advanced state, then the
 * most progress, then the name, so the table reads the same whatever the server's order.
 */
function sorted(rows: AdminRecipeRow[], sort: Sort): AdminRecipeRow[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  const byName = (a: AdminRecipeRow, b: AdminRecipeRow) => a.title.localeCompare(b.title, 'ca');
  const primary = (a: AdminRecipeRow, b: AdminRecipeRow): number => {
    switch (sort.key) {
      case 'recipe':
        return byName(a, b) * sign;
      case 'state':
        return (stateRank(a) - stateRank(b)) * sign;
      case 'progress':
        return (progressOf(a) - progressOf(b)) * sign;
      case 'cards':
        return (a.cardsRequired - b.cardsRequired) * sign;
      case 'decided':
        return (a.decidedAt ?? '').localeCompare(b.decidedAt ?? '') * sign;
    }
  };
  return [...rows].sort(
    (a, b) =>
      primary(a, b) || stateRank(b) - stateRank(a) || progressOf(b) - progressOf(a) || byName(a, b),
  );
}

/** A whole number of cards the platform accepts, or null while the field holds anything else. */
function cardsOf(text: string): number | null {
  const n = Number(text);
  return /^\d+$/.test(text.trim()) && n >= 0 && n <= 20 ? n : null;
}

/**
 * The recipes as an admin keeps them (the owner, 2026-10-12): each one's state,
 * how far its test cards have taken it, and what the platform proposes. The
 * platform only proposes; the state changes when it is changed here — the
 * proposal accepted with one click, or any state set at any time.
 *
 * Each row saves by itself. What is typed lives in a draft beside the row and
 * is dropped only when the platform answers with that same text, so an answer
 * that comes late never writes over what was typed since; and a row's changes
 * go out one after another, so each answer already carries the one before.
 */
export function RecipesTab({ onError }: Props) {
  const t = ca.adminUi.recipes;
  const [listing, setListing] = useState<AdminRecipeListing | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busy, setBusy] = useState<Record<string, number>>({});
  const queue = useRef<Record<string, Promise<void>>>({});
  // The most advanced first (the owner, 2026-10-12); a click on a heading sorts by it.
  const [sort, setSort] = useState<Sort>({ key: 'state', dir: 'desc' });

  function sortBy(key: SortKey) {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'recipe' ? 'asc' : 'desc' },
    );
  }

  function heading(key: SortKey, label: string) {
    const on = sort.key === key;
    return (
      <th aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <button type="button" className="sort" onClick={() => sortBy(key)}>
          {label}
          <i
            className={`bi bi-${on ? (sort.dir === 'asc' ? 'caret-up-fill' : 'caret-down-fill') : 'arrow-down-up'}`}
            aria-hidden="true"
          />
        </button>
      </th>
    );
  }

  useEffect(() => {
    fetchAdminRecipes()
      .then(setListing)
      .catch((e) => onError(errorText(e)));
  }, [onError]);

  function draft(id: string, change: Draft) {
    setDrafts((all) => ({ ...all, [id]: { ...all[id], ...change } }));
  }

  function send(id: string, change: Change) {
    onError(null);
    setBusy((b) => ({ ...b, [id]: (b[id] ?? 0) + 1 }));
    const run = async () => {
      try {
        const row = await updateAdminRecipe(id, change);
        setListing((l) => l && { ...l, recipes: l.recipes.map((r) => (r.id === id ? row : r)) });
        // Only what was sent, and only if nobody has typed over it since.
        setDrafts((all) => {
          const mine = { ...all[id] };
          if (change.note !== undefined && mine.note === change.note) delete mine.note;
          if (change.cardsRequired !== undefined && mine.cards === String(change.cardsRequired)) {
            delete mine.cards;
          }
          return { ...all, [id]: mine };
        });
      } catch (e) {
        onError(errorText(e));
      } finally {
        setBusy((b) => ({ ...b, [id]: (b[id] ?? 1) - 1 }));
      }
    };
    queue.current[id] = (queue.current[id] ?? Promise.resolve()).then(run);
  }

  /** The row's typed fields, as one change; null when there is nothing to send or a field is wrong. */
  function pending(row: AdminRecipeRow): Change | null {
    const mine = drafts[row.id] ?? {};
    const change: Change = {};
    if (mine.cards !== undefined && mine.cards !== String(row.cardsRequired)) {
      const n = cardsOf(mine.cards);
      if (n === null) return null;
      change.cardsRequired = n;
    }
    if (mine.note !== undefined && mine.note !== (row.note ?? '')) change.note = mine.note;
    return Object.keys(change).length ? change : null;
  }

  function saveRow(row: AdminRecipeRow) {
    const change = pending(row);
    if (change) send(row.id, change);
  }

  if (!listing) return <p className="muted">{t.loading}</p>;

  return (
    <section className="card" aria-label={t.title}>
      <h2>{t.title}</h2>
      <p className="muted small">{ca.adminUi.intros.recipes}</p>
      <div className="table-wrap">
        <table className="data recipe-states">
          <thead>
            <tr>
              {heading('recipe', t.colRecipe)}
              {heading('state', t.colState)}
              {heading('progress', t.colProgress)}
              {heading('cards', t.colCards)}
              <th>{t.colNote}</th>
              {heading('decided', t.colDecided)}
              <th>{t.colActions}</th>
            </tr>
          </thead>
          <tbody>
            {sorted(listing.recipes, sort).map((row) => {
              const mine = drafts[row.id] ?? {};
              const cards = mine.cards ?? String(row.cardsRequired);
              const note = mine.note ?? row.note ?? '';
              const saving = (busy[row.id] ?? 0) > 0;
              const change = pending(row);
              const badCards = cardsOf(cards) === null;
              const onEnter = (e: React.KeyboardEvent) => {
                if (e.key === 'Enter') saveRow(row);
              };
              return (
                <tr key={row.id} aria-label={row.title} className={row.proposal ? 'proposed' : ''}>
                  <td>
                    <span className="row who">
                      <i className={`bi bi-${row.icon || 'flag'}`} aria-hidden="true" />
                      <strong>{row.title}</strong>
                    </span>
                  </td>
                  <td>
                    <select
                      className="control"
                      aria-label={t.stateOf(row.title)}
                      value={row.state}
                      disabled={!row.generatable}
                      title={row.generatable ? undefined : t.fixed}
                      onChange={(e) => send(row.id, { state: e.target.value as RecipeState })}
                    >
                      {STATES.map((s) => (
                        <option key={s} value={s}>
                          {t.states[s]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="small">
                    {row.generatable ? (
                      <>
                        {t.progress(row.testsPassed, row.cardsRequired, row.testers)}
                        {row.testsFailed > 0 && (
                          <span className="muted"> · {t.failed(row.testsFailed)}</span>
                        )}
                        {row.proposal && (
                          <>
                            <br />
                            <span className="chip warn">{t.proposal[row.proposal]}</span>
                          </>
                        )}
                      </>
                    ) : (
                      <span className="muted">{t.fixed}</span>
                    )}
                  </td>
                  <td>
                    {row.generatable && (
                      <input
                        className="control"
                        type="number"
                        min={0}
                        max={20}
                        step={1}
                        aria-label={t.cardsOf(row.title)}
                        aria-invalid={badCards || undefined}
                        title={badCards ? t.cardsRange : undefined}
                        value={cards}
                        onChange={(e) => draft(row.id, { cards: e.target.value })}
                        onKeyDown={onEnter}
                      />
                    )}
                  </td>
                  <td>
                    {row.generatable && (
                      <input
                        className="control"
                        type="text"
                        maxLength={1024}
                        aria-label={t.noteOf(row.title)}
                        value={note}
                        onChange={(e) => draft(row.id, { note: e.target.value })}
                        onKeyDown={onEnter}
                      />
                    )}
                  </td>
                  <td className="muted small">
                    {row.generatable &&
                      (row.decided && row.decidedAt
                        ? t.decided(row.decidedBy, fmtWhen(row.decidedAt))
                        : t.followsCode)}
                  </td>
                  <td>
                    {row.generatable && (
                      <span className="row">
                        {row.proposal && (
                          <button
                            type="button"
                            className="primary"
                            disabled={saving}
                            onClick={() =>
                              send(row.id, {
                                state: row.proposal === 'ready' ? 'ready' : 'in_tests',
                              })
                            }
                          >
                            {t.accept[row.proposal]}
                          </button>
                        )}
                        <button type="button" disabled={!change} onClick={() => saveRow(row)}>
                          {saving ? t.saving : t.save}
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {listing.untiedCards > 0 && <p className="muted small">{t.untied(listing.untiedCards)}</p>}
    </section>
  );
}
